import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { MODULES, moduleName } from "@/lib/modules";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, Sparkles } from "lucide-react";

export default function LandingPage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const goDashboard = () => navigate(user ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <BrandMark size={32} />
            <div className="font-display font-medium tracking-tight text-sm">
              EMRKT<span className="text-white/60">.CH</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" data-testid="landing-login-btn" className="hq-btn hq-btn-ghost">
              {t(lang, "nav_login")}
            </Link>
            <button
              data-testid="landing-get-started-btn"
              onClick={goDashboard}
              className="hq-btn hq-btn-primary"
            >
              {t(lang, "nav_get_started")} <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hq-grid opacity-60"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[color:var(--hq-cyan)]/5 blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] px-3 py-1 rounded-full mb-8">
              <span className="hq-dot hq-dot-cyan"></span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                {t(lang, "hero_kicker")}
              </span>
            </div>
            <h1
              data-testid="landing-hero-title"
              className="font-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] whitespace-pre-line"
            >
              {t(lang, "hero_title")}
            </h1>
            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-2xl">
              {t(lang, "hero_body")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="landing-hero-primary"
                onClick={goDashboard}
                className="hq-btn hq-btn-primary text-base px-6 py-3"
              >
                {t(lang, "hero_cta_primary")} <ArrowUpRight size={16} />
              </button>
              <a href="#modules" data-testid="landing-hero-secondary" className="hq-btn hq-btn-ghost text-base px-6 py-3">
                {t(lang, "hero_cta_secondary")}
              </a>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10">
            <KpiCell value="12" label="AI modules" />
            <KpiCell value="5" label="Languages" />
            <KpiCell value="CHF 1'000" label="Target daily profit" />
            <KpiCell value="0%" label="Marketing spin" tone="cyan" />
          </div>
        </div>
      </section>

      {/* Mission / Vision Bento */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-7 hq-card p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--hq-cyan)] mb-4">
              {t(lang, "mission_kicker")}
            </div>
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight leading-tight">
              {t(lang, "mission_title")}
            </h2>
          </div>
          <div className="md:col-span-5 hq-card p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-4">
              {t(lang, "vision_kicker")}
            </div>
            <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight leading-tight">
              {t(lang, "vision_title")}
            </h2>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h2 className="font-display font-light text-3xl tracking-tight">
            {t(lang, "principles_title")}
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {["p1", "p2", "p3", "p4"].map((k) => (
            <div key={k} className="hq-card p-6">
              <div className="font-mono text-[10px] uppercase text-[color:var(--hq-cyan)] tracking-widest mb-3">
                0{k.replace("p", "")}
              </div>
              <div className="font-display text-lg mb-2">{t(lang, `${k}_title`)}</div>
              <p className="text-white/60 text-sm leading-relaxed">{t(lang, `${k}_body`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 12 AI Modules */}
      <section id="modules" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-12">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--hq-cyan)] mb-3">
            {t(lang, "modules_kicker")}
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight max-w-3xl">
            {t(lang, "modules_title")}
          </h2>
          <p className="mt-6 text-white/60 max-w-2xl">{t(lang, "modules_body")}</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {MODULES.map((m, idx) => (
            <div
              key={m.id}
              data-testid={`landing-module-${m.id}`}
              className="hq-card p-5 group cursor-default"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-9 h-9 border border-white/15 flex items-center justify-center group-hover:border-[color:var(--hq-cyan)] transition-colors duration-200">
                  <m.Icon size={16} className="text-white/80 group-hover:text-[color:var(--hq-cyan)] transition-colors duration-200" />
                </div>
                <span className="font-mono text-[10px] text-white/30">
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="font-display text-lg">{moduleName(m.id, lang)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-10">
          <h2 className="font-display font-light text-3xl tracking-tight">{t(lang, "roadmap_title")}</h2>
        </div>
        <ol className="space-y-2">
          {["roadmap_p1", "roadmap_p2", "roadmap_p3", "roadmap_p4", "roadmap_p5"].map((k, idx) => (
            <li key={k} className="hq-card p-5 flex items-center gap-4">
              <span className="font-mono text-xs text-[color:var(--hq-cyan)] w-12 shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="text-white/80 text-sm">{t(lang, k)}</span>
              {idx === 0 && (
                <span className="ml-auto font-mono text-[10px] text-[color:var(--hq-success)] uppercase tracking-widest">
                  · live
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="hq-card p-12 md:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[color:var(--hq-cyan)]/5 blur-3xl"></div>
          <div className="relative">
            <Sparkles size={22} className="text-[color:var(--hq-cyan)] mb-6" />
            <h3 className="font-display font-light text-3xl md:text-4xl tracking-tight max-w-2xl">
              {t(lang, "hero_cta_primary")}
            </h3>
            <p className="mt-4 text-white/60 max-w-xl">{t(lang, "modules_body")}</p>
            <div className="mt-8">
              <button
                data-testid="landing-cta-primary"
                onClick={goDashboard}
                className="hq-btn hq-btn-primary px-6 py-3"
              >
                {t(lang, "hero_cta_primary")} <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-black/40 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {t(lang, "footer_note")}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            v0.1.0 · phase 1
          </div>
        </div>
      </footer>
    </div>
  );
}

function KpiCell({ value, label, tone = "default" }) {
  return (
    <div className="p-6 border-r border-b border-white/10">
      <div
        className={`font-mono text-2xl md:text-3xl ${
          tone === "cyan" ? "text-[color:var(--hq-cyan)]" : "text-white"
        }`}
      >
        {value}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>
    </div>
  );
}
